import {
  FiberManualRecord as FiberManualRecordIcon,
  MoreVert as MoreVertIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Divider,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommonHelper } from 'src/utils/commonHelper';
import LinkedinShareIcon from '../../../../assets/linkedin-share-icon.svg';
import LinkedinCommentIcon from '../../../../assets/linkedin_comment.svg';
import LinkedinLikeIcon from '../../../../assets/linkedin_unlike.svg';
import LinkedinRetweetIcon from '../../../../assets/twitter_retweet.svg';
import LinkedInMediaView from '../linkedin-media-view.component.jsx';

const LinkedinPostImagesPreview = ({ data }) => {
  const { t: translate } = useTranslation();
  const [showFullDescription, setShowFullDescription] = useState(false);
  const handleToggleDescription = () => {
    setShowFullDescription(!showFullDescription);
  };

  const theme = useTheme();
  const respwidth = useMediaQuery(theme.breakpoints.up('xl'));
  const ConnectedProfiles = data.connected_profile_details;
  const ProfileTime = () => {
    return (
      <>
        <Stack direction="row" justifyContent="flex-start" alignItems="flex-start" sx={{ gap: '0.3rem' }}>
          <Typography variant="span">{translate('preview.just_now')}</Typography>
          <FiberManualRecordIcon sx={{ fontSize: 5, mt: '0.6rem' }} />
          <PublicIcon sx={{ fontSize: 15, mt: '0.3rem' }} />
        </Stack>
      </>
    );
  };

  return (
    <>
      <Stack container justifyContent="center" alignItems="center">
        <Card
          sx={{
            maxWidth: 500,
            minWidth: respwidth ? 400 : null,
            boxShadow: 3,
            borderRadius: 2,
            border: 'none',
            borderColor: theme.palette.preview.postcard.color,
          }}
        >
          <CardHeader
            avatar={
              <Avatar
                src={
                  ConnectedProfiles.connected_profile_image
                    ? ConnectedProfiles.connected_profile_image
                    : ConnectedProfiles.connected_profile_name || ConnectedProfiles.connected_profile_id
                }
                alt={
                  ConnectedProfiles.connected_profile_name
                    ? ConnectedProfiles.connected_profile_name
                    : ConnectedProfiles.connected_profile_id
                }
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  height: 40,
                  width: 40,
                  bgcolor: theme.palette.primary.main,
                }}
                style={{ color: 'white' }}
              />
            }
            title={ConnectedProfiles?.connected_profile_name}
            sx={{ fontSize: { xl: 15, lg: 13, md: 11, sm: 9, xs: 7 } }}
            subheader={<ProfileTime />}
            action={<MoreVertIcon sx={{ mt: 1 }} />}
          />
          <CardContent sx={{ pt: 1, pb: 1 }}>
            <Typography
              noWrap={!showFullDescription}
              sx={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: showFullDescription ? 'initial' : 2,
                overflow: 'hidden',
                WebkitBoxDecorationBreak: 'clone',
                whiteSpace: 'pre-line',
                textOverflow: 'ellipsis',
                fontSize: '0.875rem',
              }}
            >
              {CommonHelper.ParseDescription(data.description)}
            </Typography>
            {!showFullDescription && data.description.length > 100 && (
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  color: theme.palette.primary.main,
                }}
                onClick={handleToggleDescription}
              >
                {translate('preview.show_more')}
              </Typography>
            )}
            {showFullDescription && (
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  color: theme.palette.primary.main,
                }}
                onClick={handleToggleDescription}
              >
                {translate('preview.show_less')}
              </Typography>
            )}
          </CardContent>
          <CardMedia>
            <LinkedInMediaView data={data} />
          </CardMedia>
          <CardActions disableSpacing sx={{ display: 'block' }}>
            {data.has_comment && data.comment_description && (
              <Stack sx={{ justifyContent: 'flex-end', display: 'flex', alignItems: 'flex-end', mb: 1 }}>
                <Typography color={'text.secondary'}>1 {translate('preview.comment')}</Typography>
              </Stack>
            )}
            <Divider />
            <Stack direction="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Paper sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <img
                  src={LinkedinLikeIcon}
                  style={{ height: 'auto', width: '100%', maxWidth: '20px', maxHeight: '20px', marginLeft: '1rem' }}
                />
              </Paper>
              <Paper sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <img
                  src={LinkedinCommentIcon}
                  style={{ height: 'auto', width: '100%', maxWidth: '20px', maxHeight: '20px', marginLeft: '0.5rem' }}
                />
              </Paper>
              <Paper sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <img
                  src={LinkedinRetweetIcon}
                  style={{
                    height: 'auto',
                    width: '100%',
                    maxWidth: '20px',
                    maxHeight: '20px',
                    transform: 'rotate(90deg)',
                    marginLeft: '0.5rem',
                  }}
                />
              </Paper>
              <Paper sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <img
                  src={LinkedinShareIcon}
                  style={{ height: 'auto', width: '100%', maxWidth: '20px', maxHeight: '20px', marginRight: '1rem' }}
                />
              </Paper>
            </Stack>
            {data.has_comment && data.comment_description && (
              <>
                <Divider sx={{ mt: 0.5 }} />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    marginTop: '8px',
                  }}
                >
                  <Avatar
                    src={
                      ConnectedProfiles.connected_profile_image
                        ? ConnectedProfiles.connected_profile_image
                        : ConnectedProfiles.connected_profile_name || ConnectedProfiles.connected_profile_id
                    }
                    alt={
                      ConnectedProfiles.connected_profile_name
                        ? ConnectedProfiles.connected_profile_name
                        : ConnectedProfiles.connected_profile_id
                    }
                    sx={{
                      textAlign: 'flex-start',
                      height: 30,
                      width: 30,
                      bgcolor: theme.palette.primary.main,
                    }}
                  />
                  <Box sx={{ width: '100%' }}>
                    <Box
                      sx={{
                        padding: 1,
                        borderRadius: '8px',
                        border: `1px solid ${theme.palette.text.border}`,
                        background: theme.palette.background.default,
                      }}
                    >
                      <Stack spacing={1} direction="row" justifyContent="flex-start" alignItems="flex-start">
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {ConnectedProfiles?.connected_profile_name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'white',
                            backgroundColor: 'text.primary',
                            pl: 0.5,
                            pr: 0.5,
                            borderRadius: '3px',
                          }}
                        >
                          {translate('preview.author')}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body2"
                        style={{
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 1,
                          overflow: 'hidden',
                          WebkitBoxDecorationBreak: 'clone',
                          whiteSpace: 'pre-line',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {CommonHelper.ParseDescription(data.comment_description)}
                      </Typography>
                    </Box>
                    <Stack
                      spacing={1}
                      direction="row"
                      justifyContent="flex-start"
                      alignItems="flex-start"
                      sx={{ mt: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                        {translate('preview.like')}
                      </Typography>
                      <Divider
                        orientation="vertical"
                        variant="middle"
                        flexItem
                        sx={{
                          color: 'text.secondary',
                          borderColor: 'text.disabled',
                          borderWidth: 1,
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                        {translate('preview.reply')}
                      </Typography>
                    </Stack>
                  </Box>
                </Box>
              </>
            )}
          </CardActions>
        </Card>
      </Stack>
    </>
  );
};

export default LinkedinPostImagesPreview;
